import { keyframes, SxProps, Theme } from '@mui/material';
import { MOBILE_NAVBAR_HEIGHT } from 'src/components/Navbar/navbar.styles';

export const PEEK_NAVBAR_WIDTH_PX = 248;

export const navSlideIn = keyframes`
  from {
    transform: translateX(-0.75rem);
    opacity: 0.4;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const navPaneReveal = keyframes`
  from { opacity: 0; transform: translateY(0.5rem); }
  to { opacity: 1; transform: translateY(0); }
`;

// mirrors the redesign mockup's `panel-reveal` (6px rise, .26s, that easing);
// used by the search pane so activation + pill switches match the design
const searchPanelReveal = keyframes`
  from { opacity: 0; transform: translateY(0.375rem); }
  to { opacity: 1; transform: translateY(0); }
`;

// Scrollable pane content; the child stagger re-runs on pane switch via a `key`.
export const navPaneScrollSx: SxProps<Theme> = {
  flex: 1,
  overflow: 'auto',
  overscrollBehavior: 'contain',
  padding: '0.75rem 0.5rem 1rem',
  '&::-webkit-scrollbar': { display: 'none' },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  '& > *': { animation: `${navPaneReveal} .52s cubic-bezier(.16, 1, .3, 1) backwards` },
  '& > :nth-of-type(1)': { animationDelay: '.06s' },
  '& > :nth-of-type(2)': { animationDelay: '.14s' },
  '& > :nth-of-type(3)': { animationDelay: '.22s' },
  '& > :nth-of-type(4)': { animationDelay: '.3s' },
  '& > :nth-of-type(5)': { animationDelay: '.38s' },
  '@media (prefers-reduced-motion: reduce)': { '& > *': { animation: 'none' } },
};

// Search/Inbox panes own their own content scrolling (sticky field + chips,
// list virtualization, etc.), so their container must not scroll itself the
// way the other panes do.
export const nonScrollingNavPaneSx: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  padding: '0.75rem 0.5rem 0',
  // block-level reveal like the mockup: the field, chips and results list each
  // rise in with a short stagger. Keying the results list by entity (in the
  // component) re-runs its reveal on pill switch.
  '& > *': { animation: `${searchPanelReveal} .26s cubic-bezier(.2, .8, .2, 1) backwards` },
  '& > :nth-of-type(1)': { animationDelay: '.03s' },
  '& > :nth-of-type(2)': { animationDelay: '.07s' },
  '& > :nth-of-type(3)': { animationDelay: '.11s' },
  '@media (prefers-reduced-motion: reduce)': { '& > *': { animation: 'none' } },
};

// Sidekick pads itself so its subtab strip can run edge to edge.
export const sidekickNavPaneSx: SxProps<Theme> = {
  ...nonScrollingNavPaneSx,
  padding: '0.75rem 0 0',
};

// "More" trigger styled to read as a regular nav row (icon + body1/700 label).
export const getMoreNavButtonSx = (theme: Theme): SxProps<Theme> => ({
  width: '100%',
  minWidth: 0,
  justifyContent: 'flex-start',
  height: '1.875rem',
  borderRadius: '0.375rem',
  padding: '0 0.5rem',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  '& svg': { strokeWidth: 1.75 },
  '& .MuiTypography-root': {
    ...theme.typography.body1,
    // matches the MuiListItemText override that regular nav labels inherit
    fontSize: '0.85rem',
    fontWeight: 700,
    marginLeft: '0.625rem',
  },
  '&:hover': {
    color: theme.palette.custom.OV.sys.inverseSurface,
    backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHighest,
  },
});

export const getNavFloatShadow = (theme: Theme) =>
  theme.palette.mode === 'dark'
    ? '0 4px 12px rgba(0,0,0,.25), 0 18px 38px rgba(0,0,0,.30)'
    : '0 4px 12px rgba(0,0,0,.06), 0 14px 32px rgba(0,0,0,.10)';

export interface DrawerRootSxOptions {
  isMobile: boolean;
  isDrawerOpen: boolean;
  isPeeking?: boolean;
  navWidthPx?: number;
  /** Phone only: the global top bar sits above the drawer, so the drawer starts below it. */
  hasMobileTopBar?: boolean;
}

export const getDrawerRootSx = (
  _theme: Theme,
  {
    isMobile,
    isDrawerOpen,
    isPeeking,
    navWidthPx,
    hasMobileTopBar = true,
  }: DrawerRootSxOptions,
): SxProps<Theme> => ({
  position: { xs: 'absolute', sm: 'relative' },
  top: { xs: hasMobileTopBar ? MOBILE_NAVBAR_HEIGHT : 0, sm: 0 },
  left: 0,
  flex: 1,
  width: { xs: '75%', sm: 'auto' },
  maxWidth: { xs: '75%', sm: '100%' },
  padding: 0,
  height: {
    sm: '100%',
    xs: hasMobileTopBar ? `calc(100% - ${MOBILE_NAVBAR_HEIGHT})` : '100%',
  },
  zIndex: 4,
  ...(isMobile && {
    transform: isDrawerOpen ? 'translateX(0)' : 'translateX(-100%)',
    transition: 'transform 0.3s ease-in-out',
  }),
  ...(isPeeking && {
    position: 'fixed',
    top: '0.25rem',
    left: '0.25rem',
    width: `${(navWidthPx ?? PEEK_NAVBAR_WIDTH_PX) - 8}px`,
    maxWidth: `${(navWidthPx ?? PEEK_NAVBAR_WIDTH_PX) - 8}px`,
    height: 'calc(100vh - 0.5rem)',
    flex: 'none',
    zIndex: 35,
  }),
});

export interface DrawerPaperSxOptions {
  isPeeking?: boolean;
}

export const getDrawerPaperSx = (
  theme: Theme,
  { isPeeking }: DrawerPaperSxOptions,
): SxProps<Theme> => ({
  position: 'relative',
  border: 'none',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  flex: 1,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.custom.OV.sys.surfaceContainerHigh
      : theme.palette.custom.OV.sys.surfaceContainerLowest,
  [theme.breakpoints.up('sm')]: {
    margin: isPeeking ? 0 : '0.25rem',
    height: isPeeking ? '100%' : 'calc(100% - 0.5rem)',
    border: '1px solid',
    borderColor: theme.palette.custom.OV.sys.outlineVariant,
    borderRadius: '0.75rem',
    ...(isPeeking && {
      boxShadow: getNavFloatShadow(theme),
      animation: `${navSlideIn} 0.18s ease-out`,
    }),
  },
});
